import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles/Login.css';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import userPool from './Userpool';
import PortalHeader from './PortalHeader'; // Reuse the header component
import ResetPasswordForm from './ResetPasswordForm';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const navigate = useNavigate(); // ⬅️ React Router navigation hook

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }

    const isTestUser = email === 'test' && password === 'test';
    const isValidEmail = /\S+@\S+\.\S+/.test(email);

    if (!isTestUser && !isValidEmail) {
      setError('Please enter a valid email address.');
      return;
    }

    // ✅ TEST USER: Redirect directly
    if (isTestUser) {
      setLoading(false);
      setError('');
      // Prevent back navigation to login
      navigate('/secure-portal', { replace: true });
      return;
    }

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    setLoading(true);
    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        console.log('Login success:', result);
        setLoading(false);
        // Prevent back navigation to login
        navigate('/secure-portal', { replace: true });
      },
      onFailure: (err) => {
        console.error('Login failed:', err);
        setError(err.message || 'Login failed');
        setLoading(false);
      },
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    const { name, email, password } = registerData;
    if (!name || !email || !password) {
      setRegisterError('Name, email and password are required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setRegisterError('Please enter a valid email address.');
      return;
    }
    const allowedDomains = ["anaptyss.com", "mylut.com"];
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (!allowedDomains.includes(emailDomain)) {
      setRegisterError('Email domain not allowed. Please use your company email.');
      return;
    }
    setRegisterLoading(true);
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name })
    ];
    userPool.signUp(email, password, attributeList, null, (err, result) => {
      setRegisterLoading(false);
      if (err) {
        setRegisterError(err.message || 'Registration failed');
      } else {
        setShowVerification(true);
        setVerificationEmail(email);
        setRegisterSuccess('Registration successful! Please check your email for the verification code.');
        setRegisterData({ name: '', email: '', password: '' });
      }
    });
  };

  // Verification handler
  const handleVerify = (e) => {
    e.preventDefault();
    setVerificationError('');
    setVerificationLoading(true);
    const user = new CognitoUser({ Username: verificationEmail, Pool: userPool });
    user.confirmRegistration(verificationCode, true, (err, result) => {
      setVerificationLoading(false);
      if (err) {
        setVerificationError(err.message || 'Verification failed');
      } else {
        setShowVerification(false);
        setVerificationCode('');
        setVerificationEmail('');
        setRegisterSuccess('Verification successful! You can now log in. Redirecting to login...');
        setTimeout(() => {
          setShowRegister(false);
        }, 2000); // Show message for 2 seconds before going to login
      }
    });
  };

  // --- RESET PASSWORD FUNCTIONALITY IS TEMPORARILY DISABLED ---
  /*
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) {
      setResetError('Please enter a valid email address.');
      return;
    }
    setResetLoading(true);
    setResetError('');
    // Check with backend if user exists before proceeding
    try {
      const { checkCognitoUserExists } = await import('../api/checkUserExists');
      const exists = await checkCognitoUserExists(email);
      if (!exists) {
        setResetError('No account found with this email address.');
        setResetLoading(false);
        return;
      }
    } catch (err) {
      setResetError('Unable to verify user. Please try again.');
      setResetLoading(false);
      return;
    }
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: (data) => {
        if (!data || !data.CodeDeliveryDetails || !data.CodeDeliveryDetails.Destination) {
          setResetError('No account found with this email address.');
          setResetLoading(false);
          return;
        }
        setShowReset(true);
        setResetEmail(email);
        setResetLoading(false);
      },
      onFailure: (err) => {
        if (err.code === 'UserNotFoundException') {
          setResetError('No account found with this email address.');
        } else if (err.code === 'InvalidParameterException') {
          setResetError('This email address is not confirmed or is invalid.');
        } else {
          setResetError(err.message || 'Failed to send reset code');
        }
        setResetLoading(false);
      },
    });
  };

  const handleReset = (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    const code = e.target.code.value;
    const newPassword = e.target.newPassword.value;
    const user = new CognitoUser({ Username: resetEmail, Pool: userPool });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => {
        setShowReset(false);
        setResetLoading(false);
        setError('Password reset successful! You can now log in.');
      },
      onFailure: (err) => {
        setResetError(err.message || 'Failed to reset password');
        setResetLoading(false);
      },
    });
  };
  */

  return (
    <div className="login-container">
      <PortalHeader />
      <div className="background-animation"></div>
      <div className="login-card">
        <div className="login-header">
          <i className="fas fa-lock login-icon"></i>
          <h2>{showRegister ? 'Register for Secure Portal' : 'Secure Portal Login'}</h2>
          <p>{showRegister ? 'Create your account to access documents' : 'Enter your credentials to access documents'}</p>
        </div>
        {showReset ? (
          // <ResetPasswordForm
          //   onReset={handleReset}
          //   email={resetEmail}
          //   loading={resetLoading}
          //   error={resetError}
          // />
          <div className="error-message">Reset password is temporarily disabled.</div>
        ) : showRegister ? (
          showVerification ? (
            <form className="login-form" onSubmit={handleVerify}>
              {verificationError && <div className="error-message">{verificationError}</div>}
              <div className="input-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value)}
                  required
                  placeholder="Enter the code sent to your email"
                />
              </div>
              <button type="submit" className="login-btn" disabled={verificationLoading}>
                {verificationLoading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
          ) : (
            <form className="login-form" onSubmit={handleRegister}>
              {registerError && <div className="error-message">{registerError}</div>}
              {registerSuccess && <div className="success-message">{registerSuccess}</div>}
              <div className="input-group">
                <label htmlFor="registerName">Name</label>
                <input type="text" id="registerName" value={registerData.name} onChange={e => setRegisterData({ ...registerData, name: e.target.value })} required />
              </div>
              <div className="input-group">
                <label htmlFor="registerEmail">Email</label>
                <input type="email" id="registerEmail" value={registerData.email} onChange={e => setRegisterData({ ...registerData, email: e.target.value })} required />
              </div>
              <div className="input-group">
                <label htmlFor="registerPassword">Password</label>
                <input type="password" id="registerPassword" value={registerData.password} onChange={e => setRegisterData({ ...registerData, password: e.target.value })} required />
              </div>
              <button type="submit" className="login-btn" disabled={registerLoading}>
                {registerLoading ? 'Registering...' : 'Register'}
              </button>
              <div className="login-footer">
                <span>Already have an account? <button type="button" className="link-btn" onClick={() => setShowRegister(false)}>Log In</button></span>
              </div>
            </form>
          )
        ) : (
          <form className="login-form" onSubmit={handleLogin}>
            {error && <div className="error-message">{error}</div>}
            <div className="input-group">
              <label htmlFor="email">
                <i className="fas fa-envelope"></i> Email
              </label>
              <input
                type="text"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>
            <div className="input-group">
              <label htmlFor="password">
                <i className="fas fa-key"></i> Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              <i className="fas fa-sign-in-alt"></i>
              {loading ? 'Logging in...' : 'Log In'}
            </button>
            <div className="login-footer">
              <span>Don't have an account? <button type="button" className="link-btn" onClick={() => setShowRegister(true)}>Register</button></span>
              {/* <span>Forgot password?{' '}
                
                <button type="button" className="link-btn" onClick={handleForgotPassword} disabled={resetLoading || !email}>
                  Reset Password
                </button>
               
                <span style={{ color: '#c00', fontWeight: 500 }}>Reset password is temporarily disabled.</span>
              </span> */}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;